import React, { useState, useCallback } from 'react';
import TerminalInput from './TerminalInput';
import FeedbackPanel from './FeedbackPanel';
import { matchAnswer } from '../engine/answerMatcher';
import DomainBadge from '../../../shared/components/DomainBadge';
import { renderMarkdown } from '../../../shared/utils/markdownLite';

/**
 * Command Builder mode: present scenario, user types commands, score and review.
 */
export default function CommandBuilder({
  questions,
  onComplete,
  onUpdateStats,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [result, setResult] = useState(null);
  const [sessionResults, setSessionResults] = useState([]);
  const [startTime, setStartTime] = useState(Date.now());

  const currentQ = questions[currentIndex];

  const handleSubmit = useCallback((input) => {
    if (!currentQ) return;

    const matchResult = matchAnswer(
      input,
      currentQ.accepted_answers,
      currentQ.common_mistakes || []
    );

    const timeTaken = (Date.now() - startTime) / 1000;

    setResult(matchResult);
    setSessionResults(prev => [...prev, {
      questionId: currentQ.id,
      percentage: matchResult.percentage,
      timeTaken,
    }]);

    // Update question stats
    onUpdateStats?.(currentQ.id, matchResult.percentage === 100, timeTaken);
  }, [currentQ, startTime, onUpdateStats]);

  const handleNext = useCallback(() => {
    setResult(null);
    setStartTime(Date.now());
    if (currentIndex + 1 < questions.length) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onComplete?.(sessionResults);
    }
  }, [currentIndex, questions.length, sessionResults, onComplete]);

  // Session complete
  if (currentIndex >= questions.length && !result) {
    return null; // Parent handles completion
  }

  if (!currentQ) return null;

  const primaryAnswer = currentQ.accepted_answers.find(a => a.is_primary) || currentQ.accepted_answers[0];

  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress */}
      <div className="flex justify-between text-sm mb-2" style={{ color: '#00cc33' }}>
        <span>Question {currentIndex + 1} / {questions.length}</span>
        <span>{sessionResults.filter(r => r.percentage === 100).length} correct</span>
      </div>
      <div className="w-full bg-gray-800 rounded-full h-1 mb-6">
        <div
          className="h-1 rounded-full transition-all"
          style={{ width: `${(currentIndex / questions.length) * 100}%`, backgroundColor: '#00ff41' }}
        />
      </div>

      {/* Scenario prompt */}
      <div className="bg-gray-900 rounded-lg border border-gray-700 p-5 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <DomainBadge domainId={currentQ.domain} size="xs" />
          <span className="text-xs text-gray-500">{currentQ.category}</span>
          <span className="text-xs text-gray-600">
            {'★'.repeat(currentQ.difficulty)}{'☆'.repeat(3 - currentQ.difficulty)}
          </span>
        </div>
        <div className="text-white leading-relaxed">
          {renderMarkdown(currentQ.prompt)}
        </div>
        {currentQ.hints && currentQ.hints.length > 0 && !result && (
          <HintSystem hints={currentQ.hints} />
        )}
      </div>

      {/* Terminal input or feedback */}
      {!result ? (
        <TerminalInput
          prompt={currentQ.context_mode || 'Router#'}
          onSubmit={handleSubmit}
          multiline={true}
        />
      ) : (
        <>
          <FeedbackPanel
            result={result}
            acceptedAnswer={primaryAnswer}
          />
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-green-700 text-white rounded-lg hover:bg-green-600 font-mono text-sm"
            >
              {currentIndex + 1 < questions.length ? 'Next Question →' : 'Finish'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function HintSystem({ hints }) {
  const [revealed, setRevealed] = useState(0);

  return (
    <div className="mt-3">
      {revealed > 0 && (
        <div className="space-y-1 mb-2">
          {hints.slice(0, revealed).map((hint, i) => (
            <p key={i} className="text-sm text-yellow-400/80">💡 {hint}</p>
          ))}
        </div>
      )}
      {revealed < hints.length && (
        <button
          onClick={() => setRevealed(prev => prev + 1)}
          className="text-xs text-gray-500 hover:text-gray-400"
        >
          Show hint ({hints.length - revealed} remaining)
        </button>
      )}
    </div>
  );
}
